import * as THREE from 'three';
import { SCENE, CONTROLS } from './constants';

/**
 * Creates and returns a scene with camera and renderer
 */
const createScene = (mountRef) => {
  // Set up scene
  const scene = new THREE.Scene();
  
  // Get width of the container, not the window
  const containerWidth = mountRef.current ? mountRef.current.clientWidth : window.innerWidth;
  const containerHeight = window.innerHeight * SCENE.CONTAINER_HEIGHT_PERCENTAGE;
  
  // Create camera
  const camera = new THREE.PerspectiveCamera(
    SCENE.CAMERA.FOV, 
    containerWidth / containerHeight, 
    0.1, 
    1000
  );
  camera.position.set(
    SCENE.CAMERA.INITIAL_POSITION.X, 
    SCENE.CAMERA.INITIAL_POSITION.Y, 
    CONTROLS.ZOOM.INITIAL // Use initial zoom from constants
  );
  camera.lookAt(0, 0, 0);
  
  // Create renderer
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(containerWidth, containerHeight);
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setClearColor(0x000000); // Set background color

  // Style the canvas element to fill container without padding
  renderer.domElement.style.display = "block";
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.margin = "0";
  renderer.domElement.style.padding = "0";
  renderer.domElement.style.left = "0";
  renderer.domElement.style.position = "absolute";
  
  // Add the canvas to the DOM
  if (mountRef.current) {
    mountRef.current.appendChild(renderer.domElement);
  }
  
  // Add lights
  // Add ambient light
  const ambientLight = new THREE.AmbientLight(SCENE.LIGHTS.AMBIENT);
  scene.add(ambientLight);

  // Add directional light (like sun)
  const sunLight = new THREE.DirectionalLight(
    SCENE.LIGHTS.DIRECTIONAL.COLOR, 
    SCENE.LIGHTS.DIRECTIONAL.INTENSITY
  );
  sunLight.position.set(
    SCENE.LIGHTS.DIRECTIONAL.POSITION.X,
    SCENE.LIGHTS.DIRECTIONAL.POSITION.Y,
    SCENE.LIGHTS.DIRECTIONAL.POSITION.Z
  );
  scene.add(sunLight);
  
  return {
    scene,
    camera,
    renderer,
    containerWidth,
    containerHeight,
    ambientLight,
    sunLight,
    mountRef
  };
};

/**
 * Handles window resize events
 */
const handleResize = (sceneData) => {
  const { mountRef, camera, renderer } = sceneData;
  
  // Get width of the container, not the window
  const containerWidth = mountRef.current ? mountRef.current.clientWidth : window.innerWidth;
  const containerHeight = window.innerHeight * SCENE.CONTAINER_HEIGHT_PERCENTAGE;
  
  camera.aspect = containerWidth / containerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(containerWidth, containerHeight);
  
  // Update canvas styles on resize
  renderer.domElement.style.display = "block";
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.margin = "0";
  renderer.domElement.style.padding = "0";
  renderer.domElement.style.left = "0";
  renderer.domElement.style.position = "absolute";
};

/**
 * Cleanup function for scene objects
 */
const cleanupScene = (sceneData) => {
  const { mountRef, renderer } = sceneData;
  
  if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
    mountRef.current.removeChild(renderer.domElement);
  }
  
  renderer.dispose();
};

export { createScene, handleResize, cleanupScene };